import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachments = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getAssignmentData = (submission) => {
  const raw = submission?.assignment_data;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

export default function AssignmentReviewPage() {
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [marks, setMarks] = useState({});
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedSubmission = useMemo(
    () => submissions.find((submission) => submission.id === selectedId) || submissions[0] || null,
    [submissions, selectedId],
  );

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    if (!selectedSubmission) return;
    const nextMarks = {};
    (selectedSubmission.answers || []).forEach((answer) => {
      nextMarks[answer.questionId] =
        answer.awardedMarks ?? answer.awarded_marks ?? '';
    });
    setMarks(nextMarks);
    setRemarks(selectedSubmission.remarks || '');
  }, [selectedSubmission]);

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/lms/assignments/submissions`,
        {
          headers: getAuthHeaders(),
          params: { status: 'submitted' },
        },
      );
      const rows = response.data?.data || [];
      setSubmissions(rows);
      setSelectedId((prev) => prev || rows[0]?.id || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load assignment submissions');
    } finally {
      setLoading(false);
    }
  };

  const setQuestionMarks = (answer, value) => {
    const maxMarks = toNumber(answer.maxMarks);
    const bounded = Math.max(0, Math.min(maxMarks, toNumber(value)));
    setMarks((prev) => ({ ...prev, [answer.questionId]: bounded }));
  };

  const saveReview = async () => {
    if (!selectedSubmission) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(
        `${API_BASE_URL}/lms/assignments/submissions/${selectedSubmission.id}/review`,
        {
          remarks,
          answers: (selectedSubmission.answers || []).map((answer) => ({
            questionId: answer.questionId,
            maxMarks: toNumber(answer.maxMarks),
            awardedMarks: toNumber(marks[answer.questionId]),
          })),
        },
        { headers: getAuthHeaders() },
      );
      setSuccess('Review saved and final score updated.');
      await loadSubmissions();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const totalMarks = (selectedSubmission?.answers || []).reduce(
    (sum, answer) => sum + toNumber(answer.maxMarks),
    0,
  );
  const awardedMarks = (selectedSubmission?.answers || []).reduce(
    (sum, answer) => sum + toNumber(marks[answer.questionId]),
    0,
  );
  const selectedAssignmentData = getAssignmentData(selectedSubmission);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Assignment Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Award full marks, zero marks, or partial marks per question. Remarks and final score are saved to the submission record.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1.5}>
              {submissions.length === 0 ? (
                <Alert severity="info">No assignment submissions yet.</Alert>
              ) : null}
              {submissions.map((submission) => (
                <Card
                  key={submission.id}
                  variant={selectedSubmission?.id === submission.id ? 'elevation' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(submission.id)}
                >
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {submission.assignment_title || 'Assignment'}
                        </Typography>
                        <Chip size="small" label={submission.status} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Student: {submission.student_phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Score: {submission.awarded_marks || 0}/{submission.total_marks || 0}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            {selectedSubmission ? (
              <Card>
                <CardContent>
                  <Stack spacing={2.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {selectedSubmission.assignment_title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedSubmission.chapter_title || 'No chapter'} | Student {selectedSubmission.student_phone}
                        </Typography>
                      </Box>
                      <Chip color="primary" label={`Final: ${awardedMarks}/${totalMarks}`} />
                    </Stack>

                    <Divider />

                    {(selectedAssignmentData.answerKeyText || selectedAssignmentData.answerKeyAttachment?.fileUrl) && (
                      <Alert severity="success" sx={{ whiteSpace: 'pre-wrap' }}>
                        <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                          Answer Key
                        </Typography>
                        {selectedAssignmentData.answerKeyText || 'Answer key file attached.'}
                        {selectedAssignmentData.answerKeyAttachment?.fileUrl && (
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              href={selectedAssignmentData.answerKeyAttachment.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open Key File
                            </Button>
                          </Box>
                        )}
                      </Alert>
                    )}

                    {(selectedSubmission.answers || []).map((answer, index) => (
                      <Card variant="outlined" key={answer.questionId}>
                        <CardContent>
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {index + 1}. {answer.questionText}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Max {answer.maxMarks}
                              </Typography>
                            </Stack>
                            <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                              {answer.answerText || 'No answer entered.'}
                            </Alert>
                            {normalizeAttachments(answer.attachments).length > 0 && (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {normalizeAttachments(answer.attachments).map((attachment, attachmentIndex) => (
                                  <Button
                                    key={`${answer.questionId}-${attachmentIndex}`}
                                    size="small"
                                    variant="outlined"
                                    href={attachment.fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Open {attachment.fileName || `Attachment ${attachmentIndex + 1}`}
                                  </Button>
                                ))}
                              </Stack>
                            )}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <Button
                                variant="outlined"
                                color="success"
                                startIcon={<CheckCircleRoundedIcon />}
                                onClick={() => setQuestionMarks(answer, answer.maxMarks)}
                              >
                                Full
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<CancelRoundedIcon />}
                                onClick={() => setQuestionMarks(answer, 0)}
                              >
                                Zero
                              </Button>
                              <TextField
                                label="Partial marks"
                                type="number"
                                value={marks[answer.questionId] ?? ''}
                                onChange={(e) => setQuestionMarks(answer, e.target.value)}
                                inputProps={{ min: 0, max: answer.maxMarks, step: 0.5 }}
                                sx={{ width: { xs: '100%', sm: 180 } }}
                              />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}

                    <TextField
                      label="Assignment remarks"
                      multiline
                      minRows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" onClick={loadSubmissions} disabled={saving}>
                        Refresh
                      </Button>
                      <Button variant="contained" onClick={saveReview} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Review'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Select a submission to review.</Alert>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
